// src/pages/BookingSuccessPage.js
// Handles the return from Stripe Checkout for paid service bookings.
// Verifies payment, saves the booking to Firestore, and shows confirmation.
import React, { useState, useEffect, useRef } from 'react';
import { createBooking } from '../services/booking-service';
import { downloadICS } from '../utils/ics-generator';
import { trackEvent } from '../utils/analytics';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';
const RETRIEVE_SESSION_URL = (sessionId) => process.env.NODE_ENV === 'development'
  ? `http://localhost:5001/checkout-session/${sessionId}`
  : `${FUNCTIONS_BASE}/retrieveCheckoutSession?sessionId=${encodeURIComponent(sessionId)}`;
const PRIMARY = '#7c3aed';

// Helper: format "2026-03-05T10:15:00-08:00" → "Thursday, March 5, 2026"
function formatFullDate(isoString) {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return isoString;
  }
}

// Helper: format "2026-03-05T10:15:00-08:00" → "10:15 AM"
function formatTime(isoString) {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

// Helper: IANA timezone → short abbreviation e.g. "PST"
function getTimezoneAbbr(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

const BookingSuccessPage = () => {
  const [status, setStatus] = useState('loading'); // loading | success | error | payment_failed
  const [errorMessage, setErrorMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [_bookingId, setBookingId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_calendarSynced, setCalendarSynced] = useState(false);
  const ranRef = useRef(false);

  // Parse URL params once
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  const practitionerId = params.get('practitionerId');
  const serviceName = params.get('serviceName') || 'Session';
  const servicePrice = parseFloat(params.get('servicePrice') || '0');
  const serviceDuration = parseInt(params.get('serviceDuration') || '60', 10);
  const dateTime = params.get('dateTime') || '';
  const timezone = params.get('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientFirstName = params.get('clientFirstName') || '';
  const clientLastName = params.get('clientLastName') || '';
  const clientEmail = params.get('clientEmail') || '';
  const clientPhone = params.get('clientPhone') || '';
  const clientMessage = params.get('clientMessage') || '';
  const practitionerName = params.get('practitionerName') || 'the practitioner';

  const tzAbbr = getTimezoneAbbr(timezone);

  useEffect(() => {
    // Prevent double-run in React StrictMode
    if (ranRef.current) return;
    ranRef.current = true;

    if (!sessionId) {
      setErrorMessage('No payment session found. Please try booking again.');
      setStatus('error');
      return;
    }

    const processPayment = async () => {
      try {
        // 1. Verify payment with Stripe
        const sessionRes = await fetch(RETRIEVE_SESSION_URL(sessionId));
        if (!sessionRes.ok) throw new Error('Could not verify payment status');
        const session = await sessionRes.json();

        if (session.paymentStatus !== 'paid') {
          setStatus('payment_failed');
          return;
        }

        // 2. Save booking to Firestore + sync to Google Calendar
        let result = { bookingId: null, calendarSynced: false };
        if (practitionerId) {
          try {
            result = await createBooking({
              practitionerId,
              service: {
                name: serviceName,
                duration: serviceDuration,
                price: servicePrice,
              },
              client: {
                firstName: clientFirstName,
                lastName: clientLastName,
                email: clientEmail,
                phone: clientPhone,
                message: clientMessage,
              },
              dateTime,
              timezone,
              stripePaymentIntentId: session.paymentIntentId || null,
            });
          } catch (bookingErr) {
            // Payment succeeded but booking save failed — still show success
            // with a note to contact the practitioner
            console.error('Booking save failed after payment:', bookingErr);
            setBookingId(null);
            setCalendarSynced(false);
            setStatus('payment_success_booking_failed');
            return;
          }
        }

        setBookingId(result.bookingId);
        setCalendarSynced(result.calendarSynced);
        setStatus('success');
        trackEvent('booking_complete', {
          service_name: serviceName,
          practitioner_id: practitionerId,
          price: servicePrice,
        });
      } catch (err) {
        console.error('Error processing booking success:', err);
        setErrorMessage(err.message || 'Something went wrong. Please contact the practitioner.');
        setStatus('error');
      }
    };

    processPayment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadICS = () => {
    if (!dateTime) return;
    downloadICS({
      serviceName,
      practitionerName,
      dateTimeISO: dateTime,
      duration: serviceDuration,
    });
  };

  const handleBackToSite = () => {
    window.location.href = '/preview/website';
  };

  // ─── Shared styles ────────────────────────────────────────────────────────
  const pageStyle = {
    minHeight: '100vh',
    backgroundColor: '#F9FAFB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'Montserrat, system-ui, sans-serif',
  };

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '520px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
  };

  const detailCardStyle = {
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid rgba(124,58,237,0.12)',
    backgroundColor: 'rgba(124,58,237,0.03)',
    textAlign: 'left',
    marginBottom: '24px',
  };

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '48px', height: '48px',
            border: '3px solid #E5E7EB',
            borderTopColor: PRIMARY,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 24px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6B7280', fontSize: '15px', margin: 0 }}>Confirming your payment…</p>
        </div>
      </div>
    );
  }

  // ─── Payment failed ────────────────────────────────────────────────────────
  if (status === 'payment_failed') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            backgroundColor: '#FEF2F2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <span style={{ fontSize: '28px', color: '#EF4444' }}>✕</span>
          </div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px' }}>
            Payment Not Completed
          </h2>
          <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 32px', lineHeight: '1.6' }}>
            Your payment was not processed. No charge was made to your card.
          </p>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '12px 32px', borderRadius: '8px',
              backgroundColor: PRIMARY, color: '#FFFFFF',
              border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ─── General error ─────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            backgroundColor: '#FEF2F2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <span style={{ fontSize: '28px', color: '#EF4444' }}>!</span>
          </div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px' }}>
            Something Went Wrong
          </h2>
          <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 32px', lineHeight: '1.6' }}>
            {errorMessage || 'Please contact the practitioner to confirm your booking.'}
          </p>
          <button
            onClick={handleBackToSite}
            style={{
              padding: '12px 32px', borderRadius: '8px',
              backgroundColor: PRIMARY, color: '#FFFFFF',
              border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            Back to Site
          </button>
        </div>
      </div>
    );
  }

  // ─── Payment succeeded but booking save failed ─────────────────────────────
  if (status === 'payment_success_booking_failed') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            backgroundColor: '#FFFBEB',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <span style={{ fontSize: '28px', color: '#D97706' }}>✓</span>
          </div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px' }}>
            Payment Received
          </h2>
          <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 16px', lineHeight: '1.6' }}>
            Your payment of <strong style={{ color: '#1F2937' }}>${servicePrice.toFixed(2)}</strong> was processed successfully.
          </p>
          <div style={{
            padding: '16px', borderRadius: '10px',
            backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7',
            marginBottom: '24px', textAlign: 'left',
          }}>
            <p style={{ color: '#92400E', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
              Your booking confirmation is pending. Please contact{' '}
              <strong>{practitionerName}</strong> directly to confirm your session details.
              Your payment receipt has been sent to{' '}
              <strong>{clientEmail}</strong>.
            </p>
          </div>
          <button
            onClick={handleBackToSite}
            style={{
              padding: '12px 32px', borderRadius: '8px',
              backgroundColor: PRIMARY, color: '#FFFFFF',
              border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            Back to Site
          </button>
        </div>
      </div>
    );
  }

  // ─── Success ───────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Checkmark */}
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%',
          backgroundColor: 'rgba(124,58,237,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <span style={{ fontSize: '28px', color: PRIMARY }}>✓</span>
        </div>

        {/* Heading */}
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '26px', fontWeight: '700',
          color: '#1F2937', margin: '0 0 8px',
        }}>
          Booking Confirmed!
        </h2>

        <p style={{ color: '#6B7280', fontSize: '14px', margin: '0 0 6px' }}>
          Your session has been booked with {practitionerName}.
        </p>

        {servicePrice > 0 && (
          <p style={{
            color: '#059669', fontSize: '14px', fontWeight: '600',
            margin: '0 0 24px',
          }}>
            Payment of ${servicePrice.toFixed(2)} processed successfully
          </p>
        )}

        {/* Booking details card */}
        <div style={detailCardStyle}>
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '16px', fontWeight: '600',
            color: '#1F2937', margin: '0 0 12px',
          }}>
            {serviceName}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {dateTime && (
              <p style={{ fontSize: '14px', color: '#1F2937', margin: 0 }}>
                {formatFullDate(dateTime)}
              </p>
            )}
            {dateTime && (
              <p style={{ fontSize: '14px', color: '#1F2937', margin: 0 }}>
                {formatTime(dateTime)}{tzAbbr ? ` ${tzAbbr}` : ''} · {serviceDuration} minutes
              </p>
            )}
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
              with {practitionerName}
            </p>
          </div>
        </div>

        {/* Email notice */}
        <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '6px', lineHeight: '1.5' }}>
          A confirmation email is on its way to{' '}
          <span style={{ fontWeight: '600', color: '#1F2937' }}>{clientEmail}</span>
        </p>
        <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '32px', lineHeight: '1.5' }}>
          If you don't see it within a few minutes, check your spam or junk folder.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          {dateTime && (
            <button
              type="button"
              onClick={handleDownloadICS}
              style={{
                padding: '10px 24px', borderRadius: '8px',
                backgroundColor: 'transparent',
                color: PRIMARY, border: `1.5px solid ${PRIMARY}`,
                fontSize: '14px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.15s ease',
                width: '100%', maxWidth: '300px',
              }}
            >
              📅 Add to Your Calendar
            </button>
          )}
          <button
            type="button"
            onClick={handleBackToSite}
            style={{
              padding: '10px 32px', borderRadius: '8px',
              backgroundColor: 'transparent',
              color: '#6B7280', border: 'none',
              fontSize: '14px', cursor: 'pointer',
              transition: 'color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = PRIMARY; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          >
            ← Back to {practitionerName !== 'the practitioner' ? `${practitionerName}'s` : 'the'} Website
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
